import { MAX_DISPLAY, CARD_MARKER_Z_INDEX } from "./consts.js";
import { fetchPropertyCards } from "./api.js";
import { getTransactionType } from "./state.js";
import buttonsFavorite from "../../../components/buttons/_favorite.js";
import { countUpAction } from "../../../helpers.js";

/** @typedef {import("./api.js").Property} Property */
/** @typedef {import("./dom.js").MapPageElements} MapPageElements */

/**
 * カードから地図へ伝播させないイベント
 * stopPropagation() は既定動作を止めないため、リンク遷移やボタン操作はそのまま動く
 * @type {string[]}
 */
const STOP_PROPAGATION_EVENTS = [
    "pointerdown",
    "pointerup",
    "mousedown",
    "mouseup",
    "touchstart",
    "touchend",
    "click",
];

/**
 * 物件カードパネルを操作するインターフェース
 * @typedef {Object} CardPanel
 * @property {(property: Property) => Promise<void>} open - 物件カードを開く
 * @property {() => void} close - 物件カードを閉じる
 */

/**
 * クリックしたピンの上に表示する物件カードパネルを管理する
 * @param {Object} params
 * @param {google.maps.Map} params.map - Google Maps 本体
 * @param {MapPageElements} params.els - ページの DOM 参照一式
 * @param {typeof google.maps.marker.AdvancedMarkerElement} params.AdvancedMarkerElement - マーカークラス（map.js の initMap が返すもの）
 * @param {() => void} params.onClose - カードを閉じたときのコールバック（ピンの選択解除に使う）
 * @returns {CardPanel}
 */
export const createCardPanel = ({ map, els, AdvancedMarkerElement, onClose }) => {
    /**
     * 物件カード表示用マーカー（一度生成したら position 差し替えで再利用する）
     * @type {google.maps.marker.AdvancedMarkerElement|null}
     */
    let cardMarker = null;

    /**
     * クリックしたピンの位置にカードマーカーを配置する
     * @param {{ lat: number, lng: number }} position - 表示位置の座標
     * @returns {void}
     */
    const showCardAt = (position) => {
        if (!cardMarker) {
            cardMarker = new AdvancedMarkerElement({
                map,
                position,
                content: els.propertyContainer,
                zIndex: CARD_MARKER_Z_INDEX,
            });
        } else {
            cardMarker.position = position;
        }
    };

    /**
     * 検索ボタンの href を表示中の物件 ID で組み直す
     * @param {Array<number|string>} ids - 物件IDの一覧
     * @returns {void}
     */
    const updateSearchButton = (ids) => {
        const { searchButton } = els;
        if (!searchButton) return;

        const baseUrl = searchButton.getAttribute("href").split("?")[0];
        const params = new URLSearchParams();
        ids.forEach((id) => params.append("ids[]", id));
        searchButton.setAttribute("href", `${baseUrl}?${params.toString()}`);
    };

    /**
     * 物件カードを開く
     * @param {Property} property - 表示する物件データ
     * @returns {Promise<void>}
     */
    const open = async (property) => {
        const { propertyContainer, count } = els;

        showCardAt({
            lat: property.geopoint.lat,
            lng: property.geopoint.lng,
        });

        const limitedIds = [property.id].slice(0, MAX_DISPLAY);

        propertyContainer.innerHTML =
            '<span class="pg-search-map__property-status">読み込み中です</span>';

        try {
            const html = await fetchPropertyCards({
                ids: limitedIds,
                type: getTransactionType(),
            });
            propertyContainer.innerHTML = "";
            propertyContainer.insertAdjacentHTML("beforeend", html);
            buttonsFavorite();
        } catch (error) {
            console.error("物件カードの取得に失敗しました:", error);
            propertyContainer.innerHTML =
                '<span class="pg-search-map__property-status">データの取得に失敗しました</span>';
        }

        updateSearchButton(limitedIds);

        if (count) {
            countUpAction(count, 1);
        }
    };

    /**
     * 物件カードを閉じてピンを通常状態に戻す
     * @returns {void}
     */
    const close = () => {
        els.propertyContainer.innerHTML = "";
        onClose();
    };

    // カード内の閉じるボタン・地図クリックでカードを閉じる
    const { propertyContainer } = els;
    if (propertyContainer) {
        // カード内の操作が地図クリックとして扱われないようにする
        // SP では Google Maps がタッチ／ポインタ系イベントから地図クリックを判定するため、
        // click だけ止めてもリンクを踏む前に close() が走ってカードが消えてしまう
        STOP_PROPAGATION_EVENTS.forEach((type) => {
            propertyContainer.addEventListener(type, (event) => {
                event.stopPropagation();
            });
        });

        propertyContainer.addEventListener("click", (event) => {
            if (event.target.closest(".js-estate-map-close")) {
                close();
            }
        });
    }
    map.addListener("click", () => close());

    return { open, close };
};
